import { useState } from 'react';
import { X, Copy, Check, Link2, Clock, Lock, Eye, Trash2 } from 'lucide-react';
import { Button, Input, Label } from '@/components/ui';
import api from '@/lib/api';
import toast from 'react-hot-toast';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  pageSlug: string;
  pageTitle: string;
}

interface ShareInfo {
  token: string;
  share_url: string;
  expires_at: string | null;
  has_password: boolean;
  max_views: number | null;
  view_count: number;
}

export function ShareDialog({ isOpen, onClose, pageSlug, pageTitle }: ShareDialogProps) {
  const [expiresHours, setExpiresHours] = useState<number | undefined>(undefined);
  const [password, setPassword] = useState('');
  const [maxViews, setMaxViews] = useState<number | undefined>(undefined);
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCreateShare = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/pages/${pageSlug}/share`, {
        expires_hours: expiresHours || undefined,
        password: password || undefined,
        max_views: maxViews || undefined,
      });
      setShareInfo(response.data.data);
      toast.success('分享链接已创建');
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || '创建分享失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareInfo) return;
    try {
      await navigator.clipboard.writeText(shareInfo.share_url);
      setCopied(true);
      toast.success('链接已复制');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('复制失败');
    }
  };

  const handleRevokeShare = async () => {
    if (!shareInfo) return;
    if (!confirm('确定要撤销此分享链接吗？撤销后将无法恢复。')) return;

    try {
      await api.delete(`/shares/${shareInfo.token}`);
      setShareInfo(null);
      toast.success('分享链接已撤销');
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || '撤销失败');
    }
  };

  const resetAndClose = () => {
    setShareInfo(null);
    setExpiresHours(undefined);
    setPassword('');
    setMaxViews(undefined);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={resetAndClose} />

      {/* Dialog */}
      <div className="relative bg-card rounded-lg shadow-lg w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">分享页面</h2>
          <button onClick={resetAndClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Page title */}
        <p className="text-sm text-muted-foreground mb-4">
          分享页面: <span className="font-medium text-foreground">{pageTitle}</span>
        </p>

        {!shareInfo ? (
          <>
            {/* Settings */}
            <div className="space-y-4">
              {/* Expiration */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4" />
                  有效期
                </Label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring"
                  value={expiresHours || ''}
                  onChange={(e) => setExpiresHours(e.target.value ? Number(e.target.value) : undefined)}
                >
                  <option value="">永不过期</option>
                  <option value="1">1 小时</option>
                  <option value="24">24 小时</option>
                  <option value="168">7 天</option>
                  <option value="720">30 天</option>
                </select>
              </div>

              {/* Password */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4" />
                  访问密码（可选）
                </Label>
                <Input
                  type="password"
                  placeholder="留空则无需密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {/* Max views */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Eye className="h-4 w-4" />
                  最大访问次数（可选）
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={10000}
                  placeholder="留空则无限制"
                  value={maxViews || ''}
                  onChange={(e) => setMaxViews(e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={resetAndClose}>
                取消
              </Button>
              <Button onClick={handleCreateShare} disabled={loading}>
                {loading ? '创建中...' : '创建分享链接'}
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Share result */}
            <div className="space-y-4">
              {/* Share URL */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Link2 className="h-4 w-4" />
                  分享链接
                </Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={shareInfo.share_url}
                    className="flex-1"
                  />
                  <Button onClick={handleCopyLink} variant="outline" size="icon">
                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Share info */}
              <div className="text-sm text-muted-foreground space-y-1">
                {shareInfo.expires_at && (
                  <p>过期时间: {new Date(shareInfo.expires_at).toLocaleString()}</p>
                )}
                {shareInfo.has_password && (
                  <p className="flex items-center gap-1">
                    <Lock className="h-3 w-3" /> 已设置密码保护
                  </p>
                )}
                {shareInfo.max_views && (
                  <p>访问次数: {shareInfo.view_count} / {shareInfo.max_views}</p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between gap-2 mt-6">
              <Button variant="destructive" onClick={handleRevokeShare}>
                <Trash2 className="h-4 w-4 mr-2" />
                撤销分享
              </Button>
              <Button variant="outline" onClick={resetAndClose}>
                完成
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
